import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, ThemeProvider } from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState as PageEmptyState } from "../../components/UI/States";
import { selectUser } from "../../features/userSlice";
import { selectRoles } from "../../features/roleSlice";
import {
  SIGNAGE_COMPANY_SCOPE,
  fetchAllSignageDevices,
  fetchAllSignagePlaylists,
  fetchSignageBranches,
} from "./signageApi";
import {
  EmptyState,
  ErrorNote,
  Loading,
  SEGMENT_TRACK,
  deviceIssues,
  segmentClass,
} from "./signageUi";
import DisplaysPanel from "./SignageDisplays";
import { LibraryPicker, MediaPanel, PlaylistsPanel } from "./SignageContent";
import { BranchesPanel, SignageSettingsDialog } from "./SignageBranches";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";

// Digital Signage (/signage). Organized around how the management team
// works: Displays (every TV, its health, what it plays) → Playlists and
// Media (content, per library: company-wide or one branch) → Branches (setup,
// Admin/Executive Team only).
//
// URL: ?tab=displays|playlists|media|branches, &branch=<id> (displays
// filter), &device=<id> (open display drawer), &library=<id|company> and
// &playlist=<id> (content tabs). Old ?branch=<id>&view=<devices|playlists|
// media> links still land in the right place.

// Who can open the page at all; the API re-checks per request. Admin and
// Executive Team manage every branch; a General Manager manages the signage
// branch whose "Big C branch" matches the branch on their profile.
export const SIGNAGE_DASHBOARD_ROLES = [
  "Admin",
  "Executive Team",
  "General Manager",
];
const FULL_ACCESS_ROLES = ["Admin", "Executive Team"];

// The company-wide library, picked like a branch; the API reads its id as
// branch: null.
const COMPANY_SCOPE = {
  id: SIGNAGE_COMPANY_SCOPE,
  name: "Company-wide",
  company: true,
};

// Keeps online badges in step with the server's 2-minute window and makes
// remote-command status feel live (the Pi checks in every ~30s).
const DEVICE_REFRESH_MS = 15 * 1000;

const SignageDashboard = () => {
  const user = useSelector(selectUser);
  const role = useSelector(selectRoles);
  const roles = useMemo(() => role?.roles || [], [role]);
  const canView = SIGNAGE_DASHBOARD_ROLES.some((r) => roles.includes(r));
  const isFullAccess = FULL_ACCESS_ROLES.some((r) => roles.includes(r));
  const userId = user?.id;

  // MUI keeps its own palette — feed the in-app dark toggle into it so the
  // Selects, dialogs, and the display drawer stay legible.
  const muiTheme = useAppMuiTheme();

  const [branches, setBranches] = useState(null);
  const [devices, setDevices] = useState(null);
  const [playlists, setPlaylists] = useState(null);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const loadBranches = () =>
    fetchSignageBranches(userId)
      .then(setBranches)
      .catch((err) => setError(err.message));
  const loadDevices = () =>
    fetchAllSignageDevices(userId)
      .then(setDevices)
      .catch((err) => setError(err.message));
  const loadPlaylists = () =>
    fetchAllSignagePlaylists(userId)
      .then(setPlaylists)
      .catch((err) => setError(err.message));

  useEffect(() => {
    if (!canView || !userId) return;
    loadBranches();
    loadDevices();
    loadPlaylists();
    const timer = setInterval(loadDevices, DEVICE_REFRESH_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, userId]);

  // ---- URL state ----
  const [searchParams, setSearchParams] = useSearchParams();
  const legacyView = searchParams.get("view");
  const legacyContent = ["playlists", "media"].includes(legacyView);

  const tabs = [
    { key: "displays", label: "Displays" },
    { key: "playlists", label: "Playlists" },
    { key: "media", label: "Media" },
    ...(isFullAccess ? [{ key: "branches", label: "Branches" }] : []),
  ];
  const tabParam =
    searchParams.get("tab") || (legacyContent ? legacyView : null);
  const activeTab = tabs.some((t) => t.key === tabParam)
    ? tabParam
    : "displays";

  const scopes = useMemo(
    () => [...(isFullAccess ? [COMPANY_SCOPE] : []), ...(branches || [])],
    [isFullAccess, branches]
  );
  const libraryParam =
    searchParams.get("library") ||
    (legacyContent ? searchParams.get("branch") : null);
  const scope = scopes.find((s) => s.id === libraryParam) || scopes[0] || null;

  const branchFilter = legacyContent ? null : searchParams.get("branch");

  const location = useLocation();
  const navigate = useNavigate();

  // Every step inside the dashboard — switching tab, opening a display or
  // playlist, changing library or branch filter — is its own browser history
  // entry, so Back walks back through the dashboard instead of leaving it.
  // `replace` is only for steps that shouldn't leave a trail.
  const updateParams = (patch, { replace = false, state } = {}) => {
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    if (legacyContent) next.delete("branch");
    for (const [key, value] of Object.entries(patch)) {
      if (value == null || value === "") next.delete(key);
      else next.set(key, value);
    }
    if (next.get("tab") === "displays") next.delete("tab");
    if (next.toString() === searchParams.toString()) return; // no-op click
    setSearchParams(next, { replace, state });
  };

  // Opening a display pushes an entry marked in history state; closing it
  // then just goes Back, so the drawer doesn't leave a duplicate page behind
  // (and the browser Back button closes it too). A drawer that arrived via a
  // pasted link has no mark — close it in place instead.
  const openDevice = (id) =>
    updateParams(
      { tab: "displays", device: id, playlist: null },
      { state: { signageDrawer: true } }
    );
  const closeDevice = () => {
    if (location.state?.signageDrawer) navigate(-1);
    else updateParams({ device: null }, { replace: true });
  };

  const playlistById = useMemo(
    () => new Map((playlists || []).map((p) => [p.id, p])),
    [playlists]
  );
  const attentionCount = (devices || []).filter((d) =>
    deviceIssues(d, playlistById).some((i) => i.attention)
  ).length;

  if (!canView) {
    return (
      <PageShell size="sm">
        <PageEmptyState
          icon={TvOutlinedIcon}
          title="Digital Signage"
          description="You need an Admin, Executive Team, or General Manager role to manage signage displays."
        />
      </PageShell>
    );
  }

  // A GM can put company playlists on their displays but can't open the
  // company library, so only link to libraries this viewer has.
  const libraryOf = (playlist) =>
    playlist.company ? SIGNAGE_COMPANY_SCOPE : playlist.branch;
  const canOpenPlaylist = (playlist) =>
    scopes.some((s) => s.id === libraryOf(playlist));
  const openPlaylist = (playlist) => {
    if (!canOpenPlaylist(playlist)) return;
    updateParams({
      tab: "playlists",
      library: libraryOf(playlist),
      playlist: playlist.id,
      device: null,
    });
  };

  const scopePlaylists = scope
    ? (playlists || []).filter((p) =>
        scope.company ? p.company : p.branch === scope.id
      )
    : [];

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="app-canvas min-h-full w-full">
        <PageShell size="2xl">
          <PageHeader
            icon={TvOutlinedIcon}
            tone="violet"
            eyebrow="Admin"
            eyebrowTo="/admin"
            title="Digital Signage"
            description="The TVs around the branches — what's on each screen, and whether it's healthy. Changes reach displays within seconds."
            actions={
              isFullAccess && (
                <Button
                  variant="outlined"
                  startIcon={<SettingsOutlinedIcon />}
                  onClick={() => setShowSettings(true)}
                >
                  Settings
                </Button>
              )
            }
          >
            <nav
              role="tablist"
              aria-label="Signage sections"
              className={`mt-6 flex w-fit max-w-full overflow-x-auto ${SEGMENT_TRACK}`}
            >
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  onClick={() =>
                    updateParams({ tab: tab.key, device: null, playlist: null })
                  }
                  className={segmentClass(activeTab === tab.key, "md")}
                >
                  {tab.label}
                  {tab.key === "displays" && attentionCount > 0 && (
                    <span
                      className="min-w-[1.25rem] rounded-full bg-amber-500 px-1.5 text-center text-[11px] font-bold leading-[1.125rem] text-white tabular-nums"
                      title={`${attentionCount} display(s) need attention`}
                    >
                      {attentionCount}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </PageHeader>

          <div className="space-y-6">
            <ErrorNote>{error}</ErrorNote>
            {!branches || !playlists ? (
              <Loading />
            ) : (
              <>
                {activeTab === "displays" && (
                  <DisplaysPanel
                    branches={branches}
                    devices={devices}
                    playlists={playlists}
                    userId={userId}
                    isFullAccess={isFullAccess}
                    branchFilter={branchFilter}
                    onBranchFilter={(id) => updateParams({ branch: id })}
                    openDeviceId={searchParams.get("device")}
                    onOpenDevice={(id) => (id ? openDevice(id) : closeDevice())}
                    onOpenPlaylist={openPlaylist}
                    canOpenPlaylist={canOpenPlaylist}
                    onGoToBranches={() => updateParams({ tab: "branches" })}
                    onChanged={loadDevices}
                  />
                )}

                {(activeTab === "playlists" || activeTab === "media") &&
                  (scope ? (
                    <>
                      <LibraryPicker
                        scopes={scopes}
                        value={scope.id}
                        onChange={(id) =>
                          updateParams({ library: id, playlist: null })
                        }
                      />
                      {activeTab === "playlists" ? (
                        <PlaylistsPanel
                          scope={scope}
                          playlists={playlists}
                          devices={devices}
                          branches={branches}
                          userId={userId}
                          selectedId={searchParams.get("playlist")}
                          onSelect={(id) => updateParams({ playlist: id })}
                          onOpenDevice={openDevice}
                          onChanged={loadPlaylists}
                        />
                      ) : (
                        <MediaPanel
                          key={scope.id}
                          scope={scope}
                          playlists={scopePlaylists}
                          userId={userId}
                          onChanged={loadPlaylists}
                        />
                      )}
                    </>
                  ) : (
                    <EmptyState
                      icon={<TvOutlinedIcon />}
                      title="No signage branch yet"
                    >
                      No signage branch is set up for your Big C branch yet — ask
                      an admin to create one.
                    </EmptyState>
                  ))}

                {activeTab === "branches" && isFullAccess && (
                  <BranchesPanel
                    branches={branches}
                    devices={devices}
                    playlists={playlists}
                    userId={userId}
                    onViewDisplays={(b) =>
                      updateParams({ tab: "displays", branch: b.id })
                    }
                    onViewPlaylists={(b) =>
                      updateParams({
                        tab: "playlists",
                        library: b.id,
                        playlist: null,
                      })
                    }
                    onChanged={() => {
                      loadBranches();
                      loadDevices();
                      loadPlaylists();
                    }}
                  />
                )}
              </>
            )}
          </div>
        </PageShell>
      </div>

      {showSettings && (
        <SignageSettingsDialog
          userId={userId}
          branches={branches || []}
          devices={devices || []}
          onClose={() => setShowSettings(false)}
        />
      )}
    </ThemeProvider>
  );
};

export default SignageDashboard;
